// Dynamic backend URL with MULTI-HOST FAILOVER (Feb 2026)
//
// Reality: a single backend host = a single point of failure. The May 19
// Railway outage took flavorfeed.ai down even though MongoDB and our code
// were perfectly healthy. The fix: probe a LIST of candidate backends in
// parallel and use whichever responds first with the FlavorFeed health
// signature. If the primary later dies mid-session, an axios interceptor
// (see posApi.js) detects the timeout/5xx and re-probes.
//
// Configure candidates via `REACT_APP_BACKEND_URLS` (comma-separated). Falls
// back to `REACT_APP_BACKEND_URL` (single) for backward compat.
//
// Examples for production:
//   REACT_APP_BACKEND_URLS=https://api.flavorfeed.ai,https://flavorfeed-api.onrender.com,https://flavorfeed-api.fly.dev
//
// The page loads with the cached "last known good" host instantly (zero
// startup delay) and silently swaps if a probe finds a faster/healthier one.

const STORAGE_KEY = 'ff_backend_url_override';
const STORAGE_TIMESTAMP_KEY = 'ff_backend_url_override_ts';
const CACHE_TTL_MS = 5 * 60 * 1000;       // re-probe after 5 min idle
const PROBE_TIMEOUT_MS = 2_500;            // fail a candidate fast

/** Build the candidate list, deduped and in priority order.
 *
 *  ⚡ Custom-domain rule (May 2026 bug fix):
 *  -------------------------------------------------------------------
 *  Tenants serving the app on their own domain (`mandi-cafe.flavorfeed.ai`,
 *  `pos.someshop.com`, etc.) have an ingress that routes `/api/*` to THEIR
 *  backend, which talks to THEIR MongoDB. The build-time
 *  `REACT_APP_BACKEND_URL` is the generic preview ingress, talking to a
 *  DIFFERENT DB. Racing the two means login goes to a random DB and the
 *  user's account either exists or doesn't — explaining the long-standing
 *  intermittent "Invalid email or password" + "hard reset sometimes works"
 *  bug report. On a custom domain we ALWAYS prefer same-origin and never
 *  race against the preview.
 *
 *  The canonical FlavorFeed marketing domain (`flavorfeed.ai`,
 *  `www.flavorfeed.ai`) still uses multi-host failover because we
 *  intentionally run the same backend behind several CDNs for HA.
 */
const _candidates = () => {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const singleEnv = (process.env.REACT_APP_BACKEND_URL || '').trim();
  const multiEnv = (process.env.REACT_APP_BACKEND_URLS || '').trim();
  const envHostname = (() => {
    try { return singleEnv ? new URL(singleEnv).hostname : ''; } catch { return ''; }
  })();

  // Customer-controlled domains: anything that isn't localhost, isn't the
  // canonical flavorfeed.ai marketing site, and isn't the same host as the
  // build-time REACT_APP_BACKEND_URL. On these, the operator has their own
  // backend behind their own ingress — same-origin is the only correct
  // answer. Returning a single-item list also skips the parallel race in
  // ensureHealthyBase() so login is deterministic.
  const isCanonicalMarketing = hostname === 'flavorfeed.ai' || hostname === 'www.flavorfeed.ai';
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  const isPreviewEnvHost = hostname === envHostname;
  if (!isCanonicalMarketing && !isLocalhost && !isPreviewEnvHost && origin) {
    return [origin.replace(/\/+$/, '')];
  }

  const list = [];
  if (multiEnv) {
    multiEnv.split(',').map((s) => s.trim()).filter(Boolean).forEach((u) => list.push(u));
  }
  if (singleEnv) list.push(singleEnv);

  if (isCanonicalMarketing) {
    list.push('https://api.flavorfeed.ai');                                  // Railway (primary)
    list.push('https://flavorfeed-api.onrender.com');                        // Render (failover #1)
    list.push('https://flavorfeed-api.fly.dev');                             // Fly.io (failover #2)
    list.push('https://flavor-integrations.preview.emergentagent.com');       // Emergent (last resort)
  }
  if (isLocalhost) {
    const isPOSPage = window.location.pathname.includes('/pos') ||
                      window.location.pathname.includes('/admin') ||
                      window.location.pathname.includes('/kitchen');
    list.push(isPOSPage ? 'http://localhost:8002' : 'http://localhost:8001');
  }
  // Same-origin safety net (only used when we're NOT in the custom-domain
  // branch above — i.e. preview build, canonical marketing, or localhost).
  if (origin) list.push(origin);

  // Dedupe preserving order
  return Array.from(new Set(list.map((s) => s.replace(/\/+$/, ''))));
};

// In-memory cache so we don't redo the probe on every API call.
let _cachedBase = null;
let _probePromise = null;

const _probeOne = async (url) => {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT_MS);
    const res = await fetch(`${url}/api/health`, {
      method: 'GET',
      signal: ctrl.signal,
      credentials: 'omit',
      redirect: 'manual', // avoid Vercel www-redirect hang
      cache: 'no-store',
    });
    clearTimeout(t);
    if (!res.ok && res.status !== 0) return null;
    const txt = await res.text();
    if (!/"service"\s*:\s*"flavorfeed-backend"/.test(txt)) return null;
    return url;
  } catch {
    return null;
  }
};

/**
 * Race ALL candidates in parallel — first healthy wins. This is the key to
 * sub-second failover when the primary is down. Falls back to the cached
 * "last known good" if literally everything fails (better than nothing).
 */
const _raceProbes = async (cands) => {
  if (!cands.length) return null;
  // Promise.any returns the first fulfilled (non-null) result.
  return new Promise((resolve) => {
    let pending = cands.length;
    let resolved = false;
    cands.forEach((url) => {
      _probeOne(url).then((ok) => {
        if (ok && !resolved) {
          resolved = true;
          resolve(ok);
          return;
        }
        pending -= 1;
        if (pending === 0 && !resolved) resolve(null);
      });
    });
  });
};

const _persisted = () => {
  try {
    const v = sessionStorage.getItem(STORAGE_KEY);
    const ts = parseInt(sessionStorage.getItem(STORAGE_TIMESTAMP_KEY) || '0', 10);
    if (!v) return null;
    if (Date.now() - ts > CACHE_TTL_MS) return null;
    // ⚡ Custom-domain self-heal (May 2026): if the persisted host isn't in
    // the CURRENT candidate list (e.g. a stale Emergent-preview entry
    // saved before the custom-domain fix shipped), discard it so the next
    // call re-picks a correct backend. Without this, users who hit the
    // bug before the patch was deployed stay stuck until they clear
    // sessionStorage manually.
    const validHosts = new Set(_candidates());
    const normalized = v.replace(/\/+$/, '');
    if (!validHosts.has(normalized)) {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_TIMESTAMP_KEY);
      return null;
    }
    return v;
  } catch {
    return null;
  }
};

const _savePersisted = (url) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, url);
    sessionStorage.setItem(STORAGE_TIMESTAMP_KEY, String(Date.now()));
  } catch { /* ignore */ }
};

/**
 * Sync URL getter — uses cache if available, else returns the first
 * candidate as best-effort. Callers that can afford to wait should
 * `await ensureHealthyBase()` instead.
 */
const getBackendUrl = () => {
  if (_cachedBase) return _cachedBase;
  const persisted = _persisted();
  if (persisted) {
    _cachedBase = persisted;
    return persisted;
  }
  const cands = _candidates();
  return cands[0] || (typeof window !== 'undefined' ? window.location.origin : '');
};

/**
 * Probe all candidates in parallel, pick the first healthy one, persist
 * the choice. Subsequent calls return the cached result until the TTL
 * expires (5 min) or `resetBackendUrlCache()` is called.
 *
 * Returns the resolved base URL.
 */
export const ensureHealthyBase = async () => {
  if (_cachedBase) return _cachedBase;
  const persisted = _persisted();
  if (persisted) {
    _cachedBase = persisted;
    return persisted;
  }

  if (_probePromise) return _probePromise; // dedupe concurrent calls

  const cands = _candidates();
  _probePromise = (async () => {
    const winner = await _raceProbes(cands);
    const chosen = winner || cands[0] || window.location.origin;
    _cachedBase = chosen;
    _savePersisted(chosen);
    if (!winner) {
      // eslint-disable-next-line no-console
      console.warn('[FlavorFeed] No healthy backend found; using best-guess', chosen);
    } else {
      // eslint-disable-next-line no-console
      console.info('[FlavorFeed] Backend selected:', chosen);
    }
    _probePromise = null;
    return chosen;
  })();
  return _probePromise;
};

/**
 * Called by the axios error interceptor when the current backend stops
 * responding mid-session. Forces a fresh race + new winner. The next API
 * call from the page will hit the new host transparently.
 */
export const failoverToNextHealthy = async () => {
  const previous = _cachedBase;
  _cachedBase = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_TIMESTAMP_KEY);
  } catch { /* ignore */ }
  // Re-race, excluding the now-known-bad previous winner.
  const cands = _candidates().filter((u) => u !== previous);
  if (!cands.length) {
    _cachedBase = previous;       // nothing better — stick with what we had
    return previous;
  }
  const winner = await _raceProbes(cands);
  const chosen = winner || cands[0];
  _cachedBase = chosen;
  _savePersisted(chosen);
  // eslint-disable-next-line no-console
  console.warn(`[FlavorFeed] Failover: ${previous} → ${chosen}`);
  return chosen;
};

/** Force-clear the cached base (e.g. on logout or env switch). */
export const resetBackendUrlCache = () => {
  _cachedBase = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem(STORAGE_TIMESTAMP_KEY);
  } catch { /* ignore */ }
};

/** Exposed for diagnostics — useful for the "Backend Status" banner. */
export const getCandidatesForDebug = () => _candidates();

export default getBackendUrl;
